import {
  ClassNameMap,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
} from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { Fragment, ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useCallback } from 'react';
import { SvgIconProps } from '@mui/material';
import { AuthorizationItems } from 'shell/models/authorizations';
import { useMenuItemsAuthorization } from './use-menu-item-authorization';
import {
  ConsulateAuthorization,
  GroupAuthorization,
  ProfileAuthorization,
  ProvinceAuthorization,
  RegionAuthorization,
  ForeignStateAuthorization,
  TerritoryAuthorization,
  MunicipalityAuthorization,
  FeatureAuthValueAuthorization,
  FeatureAuthAuthorization,
  ToponymyAuthorization,
  EventTypeAuthorization,
  DestinationTypeAuthorization,
  SubscriberAuthorization,
  EventRegistrationAuthorization,
  SearchPublishedEventsAuthorization,
  SearchErrorEventsAuthorization,
  ProcessingJobAuthorization,
  BatchManagementAuthorization,
  ManagementNonRecoverableErrorsAuthorization,
  PecManagementAuthorization,
  NumeratorAuthorization,
} from 'shell/authorizations/system';
import { Feature } from 'shell/models/feature';

export const AuthorizationSystemMenu = Object.freeze({
  group: GroupAuthorization,
  profile: ProfileAuthorization,
  numerator: NumeratorAuthorization,
  municipality: MunicipalityAuthorization,
  region: RegionAuthorization,
  province: ProvinceAuthorization,
  state: ForeignStateAuthorization,
  territory: TerritoryAuthorization,
  consulate: ConsulateAuthorization,
  featureAuthValue: FeatureAuthValueAuthorization,
  featureAuth: FeatureAuthAuthorization,
  toponymy: ToponymyAuthorization,
  eventType: EventTypeAuthorization,
  destinationType: DestinationTypeAuthorization,
  subscriber: SubscriberAuthorization,
  eventRegistration: EventRegistrationAuthorization,
  searchPublishedEvents: SearchPublishedEventsAuthorization,
  managementNonRecoverableErrors: ManagementNonRecoverableErrorsAuthorization,
  searchEventsInError: SearchErrorEventsAuthorization,
  job: ProcessingJobAuthorization,
  batch: BatchManagementAuthorization,
  batchUploadFile: BatchManagementAuthorization,
  batchDownloadFile: BatchManagementAuthorization,
  pecManagement: PecManagementAuthorization,
});

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    nested: {
      paddingLeft: `${theme.spacing(4)} !important`,
    },
    nestedIcon: {
      minWidth: 'auto !important',
      paddingRight: theme.spacing(2),
      // color: `${theme.palette.common.white} !important`,
      color: '#555 !important',
    },
    colorItems: {
      // color: `${theme.palette.common.white} !important`,
      color: '#555 !important',
    },
  })
);

export type NestedItems = (props: { link: string; onSubItemClick?: () => void }) => JSX.Element;

interface ItemProps {
  label: string;
  link: string;
  nestedItems?: NestedItems;
  authorizations?: AuthorizationItems;
  abilitations?: { [key in keyof Feature]: Array<string> };
  icon?: (props: SvgIconProps) => JSX.Element;
  onSubItemClick?: () => void;
}

export interface CollapseItemProps extends ItemProps {
  subItems?: ItemProps[];
  nestedItems?: NestedItems;
}

const CollapseItem = ({
  label,
  icon: Icon,
  subItems = [],
  link,
  onSubItemClick,
  nestedItems: NestedItems,
}: CollapseItemProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const classes: ClassNameMap<string> = useStyles();

  // Menu active items hook
  const activeItems = useMenuItemsAuthorization(subItems);

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  return (
    <Fragment>
      <ListItem
        button
        key={label}
        onClick={() => {
          handleClick();
        }}
        className={classes.colorItems}
      >
        <ListItemIcon className={classes.colorItems}>{Icon ? <Icon /> : null}</ListItemIcon>
        <ListItemText className={classes.colorItems} primary={label} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {activeItems?.map(({ icon: SubItemIcon, ...subItem }) => {
            if (subItem.nestedItems) {
              return (
                <subItem.nestedItems
                  key={`${link}/${subItem.link}`}
                  link={link}
                  onSubItemClick={onSubItemClick}
                />
              );
            }
            if (!subItem.label) {
              return;
            }
            return (
              <Link
                key={`${link}/${subItem.link}`}
                to={`${link}/${subItem.link}`}
                className="pmi-anchor"
              >
                <ListItem
                  button
                  key={subItem.label}
                  onClick={onSubItemClick}
                  className={classes.nested}
                >
                  <ListItemIcon className={classes.nestedIcon}>
                    {SubItemIcon ? <SubItemIcon /> : null}
                  </ListItemIcon>
                  <ListItemText className={classes.colorItems} primary={subItem.label} />
                </ListItem>
              </Link>
            );
          })}
          {NestedItems ? <NestedItems link={link} onSubItemClick={onSubItemClick} /> : undefined}
        </List>
      </Collapse>
    </Fragment>
  );
};

CollapseItem.displayName = 'Collapsable Item';

export default CollapseItem;
